import { useEffect, useMemo, useState } from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useHistory } from '../../../providers/HistoryProvider'
import { FileUploader } from '../../uploadComponents/FileUploader'
import { useAuth } from '@clerk/clerk-react'
import { GetUploadAudioSignedURL } from '../../../supabase/supabaseProxy'
import { uploadRecordingToSupabase } from '@neum/frontend-shared/supabase/supabaseUtils'
import { v4 as uuidv4 } from 'uuid';
import { mimeTypeExtensionMap } from '../../../utils/RecordingUtils'
import va from "@vercel/analytics";
import { Spinner } from '../../../utils/Spinner'
import { RecordingFileStatus, updatedSession } from '../../../ServerActions'
import VetRecButton from '../../common/VetRecButton'
import { BackButton } from '../../common/Modal'
import { TemplateObject } from '../../../utils/TemplateObject'
import { RecordingUploadMode } from '../../../utils/UploadUtils'

interface UploadRecordingMainPageProps {
    onBack?: () => void;
    upstreamNoteId?: string;
    template: TemplateObject | undefined;
    onClose: () => void;
    mode: RecordingUploadMode;
}

export const UploadRecordingMainPage: React.FC<UploadRecordingMainPageProps> = ({ 
    onBack,
    upstreamNoteId,
    template,
    onClose,
    mode,
}) => {
    const {
        activeSession, 
        uploadRecordingModal, 
        setUploadRecordingModal, 
        refreshActiveSession,
        multiDocEligible,
        allNotesMetadata,
        processNotes,
        registerUploadedAudio,
    } = useHistory()

    const { getToken } = useAuth()
    const [filesToUpload, setFilesToUpload] = useState<File[]>([])
    const [uploadedFiles, setUploadedFiles] = useState<string[]>([])
    const [uploading, setUploading] = useState<boolean>()
    const [uploadingFile, setUploadingFile] = useState<string>()
    const [uploadPercentage, setUploadPercentage] = useState<number>(0) 
    const [uploadFailed, setUploadFailed] = useState<boolean>(false)
    const [uploadSuccess, setUploadSuccess] = useState<boolean>(false)

    const selectedNoteId = useMemo(() => upstreamNoteId ?? uuidv4(), [upstreamNoteId])

    const activeDocument = useMemo(() => {
        return allNotesMetadata?.find(doc => doc.id === selectedNoteId);
    }, [allNotesMetadata, selectedNoteId]);

    useEffect(() => {
        setUploadFailed(false);
        setUploadedFiles([]);
        setFilesToUpload([]);
        setUploading(false);
        setUploadingFile(undefined);
        setUploadPercentage(0);
        setUploadSuccess(false);
    }, [uploadRecordingModal])

    const uploadFile = async (file: File) => {
        //Retry getting token
        let token = await getToken({ template: "supabase" });
        if(!token){
            token = await getToken({ template: "supabase" }) ?? "";
        }

        const maxRetries = 3; // Max retry attempts
        let attempt = 0;
        let success = false;

        const mimeType = file.type;
        const fileExtension = mimeTypeExtensionMap[mimeType];
        const sessionId = activeSession?.id ?? "";
        const recordingId = uuidv4();

        // Upload using signed URL
        while (attempt < maxRetries && !success) {
            try {
                if (multiDocEligible) {
                    await registerUploadedAudio(recordingId, selectedNoteId, template);
                }

                const signedUrl = await GetUploadAudioSignedURL(sessionId, recordingId, fileExtension.split(".")[1], token);
                await uploadRecordingToSupabase({file, signedUrl, contentType: mimeType, sessionId, setUploadPercentage });
                success = true; // Break loop on success
                await RecordingFileStatus(sessionId, recordingId, token);
            } catch (error) {
                attempt++;
                let errorMessage = "An unknown error occurred";
                if (error instanceof Error) {
                    errorMessage = error.message;
                }
                if (attempt >= maxRetries) {
                    setUploadFailed(true);
                    va.track("Recording_Manual_Upload_Failed", {
                        date: new Date().toUTCString(),
                        sessionId: sessionId,
                        error: errorMessage
                    });
                } else {
                    const backoffTime = 500 * Math.pow(2, attempt); // Exponential backoff
                    await new Promise(resolve => setTimeout(resolve, backoffTime));
                }
            }
        }
    }

    const onSubmitButtonClick = async (files: File[]) => {
        if(!activeSession){
            return;
        }
        const token = await getToken({ template: "supabase" });
        setFilesToUpload(files);
        setUploadFailed(false);
        setUploading(true);
        for (let i = 0; i < files.length; i++) {
            setUploadingFile(files[i].name);
            await uploadFile(files[i]);
            setUploadedFiles((previous) => [...previous, files[i].name]);
            setUploadingFile(undefined);
            setUploadPercentage(0);
        }
        await updatedSession(activeSession.id, token ?? "")
        await refreshActiveSession()
        setUploadSuccess(true);
        setUploading(false);
    }

    async function onProcessClicked() {
        await processNotes(mode === RecordingUploadMode.ADD_TO_EXISTING_NOTE, selectedNoteId, undefined, template)
        setUploadRecordingModal(false)
        onClose()
    }

    return (
        <div>
            {multiDocEligible && !uploadSuccess && !uploading && onBack && <BackButton onClick={onBack} />}
            {multiDocEligible && <div className="flex justify-center">
                <div className="bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">
                        <span>{mode === RecordingUploadMode.CREATE_NEW_NOTE ? "Create a new note with template " : "Add recording to "}</span> 
                        <span onClick={onBack} className="font-semibold text-blue-600 cursor-pointer">{(mode === RecordingUploadMode.CREATE_NEW_NOTE ? template?.name : activeDocument?.title) || 'None'}</span>
                    </p>
                </div>
            </div>}
            {uploading && <div className="mt-4 flex flex-col gap-y-2">
                {filesToUpload.length > 0 && filesToUpload.map((file) => (
                    <div key={file.name} className='flex justify-between'>
                        <span className='truncate w-60'>{file.name}</span>
                        {uploadingFile === file.name && <span>{uploadPercentage}%</span>}
                        {uploadingFile !== file.name && !uploadedFiles.includes(file.name) && <Spinner size='h-4 w-4' timer={false}/>}
                        {uploadedFiles.includes(file.name) && <CheckCircleIcon className="h-4 w-4 text-green-600" aria-hidden="true" />}
                    </div>
                ))}
            </div>}
            {!uploading && !uploadSuccess && <FileUploader sessionId={activeSession?.id} onSubmitButtonClick={onSubmitButtonClick} submitButtonText='Upload'/>}
            {uploadSuccess && <div className='w-full flex flex-col justify-center mt-4'>
                <span className="text-sm font-semibold text-green-600 w-full flex flex-row items-center gap-x-2 justify-center">
                    Upload successful. <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                </span>
            </div>}
            {uploadFailed && <div className='w-full flex justify-center mt-4'>
                <span className="text-xs text-red-600">
                    Upload failed. Please try again.
                </span>
            </div>}
            <div>
                {multiDocEligible && uploadSuccess && <VetRecButton
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-main-button px-3 py-2 text-sm font-semibold text-main-button-text shadow-sm hover:bg-main-button-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 mt-4"
                    onClick={onProcessClicked}
                    showProcessing
                    processingText="Processing..."
                >
                    Process
                </VetRecButton>}
                <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 mt-4"
                    onClick={() => setUploadRecordingModal(false)}
                >
                    Close
                </button>
            </div>
        </div>    
    )
}